import { ANNOTATION_SOURCE_LOCATION, type Entity } from '@backstage/catalog-model';

const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

const isValidSlug = (slug: string): boolean => /^[^/\s]+\/[^/\s]+$/.test(slug);

/**
 * Derives the GitHub `owner/repo` slug for a marketplace AiResource so the
 * install instructions can reference the concrete repository.
 *
 * Prefers the `github.com/project-slug` annotation (already `owner/repo`) and
 * falls back to parsing the repository path out of the
 * `backstage.io/source-location` URL. Returns undefined when neither yields a
 * usable slug, letting the card render a generic placeholder instead.
 */
export const getMarketplaceRepoSlug = (entity: Entity): string | undefined => {
  const annotations = entity.metadata.annotations ?? {};

  const projectSlug = annotations[PROJECT_SLUG_ANNOTATION]?.trim();
  if (projectSlug && isValidSlug(projectSlug)) {
    return projectSlug;
  }

  const sourceLocation = annotations[ANNOTATION_SOURCE_LOCATION]?.trim();
  if (sourceLocation) {
    // Strip the Backstage location type prefix (e.g. "url:") if present.
    const withoutType = sourceLocation.replace(/^[a-z-]+:/i, '');
    const match = /github\.com\/([^/\s]+)\/([^/\s]+)/i.exec(withoutType);
    if (match) {
      const owner = match[1];
      const repo = match[2].replace(/\.git$/i, '');
      return `${owner}/${repo}`;
    }
  }

  return undefined;
};
